import { useState, useEffect, useCallback } from 'react';
import {
  Store,
  Search,
  Star,
  Download,
  Trash2,
  Loader2,
  AlertCircle,
  X,
  CheckCircle2,
  Shield,
  Award,
} from 'lucide-react';
import clsx from 'clsx';
import type { MarketplaceSkill } from '@/types';
import { marketplaceApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Category Config ---------------------------------------------------------

const categoryConfig: Record<string, { bg: string; text: string }> = {
  Consolidation: { bg: 'bg-blue-50', text: 'text-blue-700' },
  Tax: { bg: 'bg-green-50', text: 'text-green-700' },
  Audit: { bg: 'bg-purple-50', text: 'text-purple-700' },
  Analytics: { bg: 'bg-orange-50', text: 'text-orange-700' },
  Compliance: { bg: 'bg-red-50', text: 'text-red-700' },
};

// --- Star Rating -------------------------------------------------------------

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={clsx(
            'w-3.5 h-3.5',
            i < Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200',
          )}
        />
      ))}
      <span className="ml-1 text-xs text-gray-500 font-mono">{rating.toFixed(1)}</span>
    </div>
  );
}

// --- Skill Detail Modal ------------------------------------------------------

interface SkillDetailModalProps {
  skill: MarketplaceSkill;
  onClose: () => void;
  onInstall: (skillId: string) => Promise<void>;
  onUninstall: (skillId: string) => Promise<void>;
}

function SkillDetailModal({ skill, onClose, onInstall, onUninstall }: SkillDetailModalProps) {
  const [loading, setLoading] = useState(false);

  const handleAction = async () => {
    setLoading(true);
    try {
      if (skill.is_installed) {
        await onUninstall(skill.skill_id);
      } else {
        await onInstall(skill.skill_id);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-900">{skill.name}</h3>
            {skill.is_official && (
              <span className="badge bg-blue-50 text-blue-700 text-[10px] gap-0.5">
                <Shield className="w-2.5 h-2.5" /> Official
              </span>
            )}
            {skill.is_certified && (
              <span className="badge bg-purple-50 text-purple-700 text-[10px] gap-0.5">
                <Award className="w-2.5 h-2.5" /> Certified
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-700">{skill.description}</p>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Version</p>
              <p className="text-sm font-mono font-medium text-gray-900">{skill.version}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Category</p>
              <span className={clsx('badge text-xs', categoryConfig[skill.category]?.bg || 'bg-gray-100', categoryConfig[skill.category]?.text || 'text-gray-700')}>
                {skill.category}
              </span>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Installs</p>
              <p className="text-sm font-mono font-medium text-gray-900">{skill.install_count.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Rating</p>
              <StarRating rating={skill.rating} />
            </div>
          </div>

          {skill.author && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Author</p>
              <p className="text-sm font-medium text-gray-900">{skill.author}</p>
            </div>
          )}

          {skill.requirements && skill.requirements.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">Requirements</p>
              <ul className="space-y-1">
                {skill.requirements.map((req, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    {req}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button onClick={onClose} className="btn-secondary text-sm">Close</button>
          <button
            onClick={handleAction}
            disabled={loading}
            className={clsx(
              'text-sm font-medium px-4 py-2 rounded-lg transition-colors flex items-center',
              skill.is_installed
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-primary-700 text-white hover:bg-primary-800',
            )}
          >
            {loading && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {skill.is_installed ? (
              <><Trash2 className="w-4 h-4 mr-1.5" /> Uninstall</>
            ) : (
              <><Download className="w-4 h-4 mr-1.5" /> Install</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Tab Type ----------------------------------------------------------------

type MarketplaceTab = 'catalog' | 'installed';

// --- Category List -----------------------------------------------------------

const categories = ['All', 'Consolidation', 'Tax', 'Audit', 'Analytics', 'Compliance'];

// --- Main Page ---------------------------------------------------------------

export default function Marketplace() {
  const { addNotification } = useAppStore();

  const [activeTab, setActiveTab] = useState<MarketplaceTab>('catalog');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [skills, setSkills] = useState<MarketplaceSkill[]>([]);
  const [installed, setInstalled] = useState<MarketplaceSkill[]>([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedSkill, setSelectedSkill] = useState<MarketplaceSkill | null>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [skillsRes, installedRes] = await Promise.all([
        marketplaceApi.list(),
        marketplaceApi.installed(),
      ]);
      setSkills(skillsRes);
      setInstalled(installedRes);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load marketplace');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInstall = async (skillId: string) => {
    try {
      await marketplaceApi.install(skillId);
      addNotification({ type: 'success', title: 'Skill Installed', message: 'The skill has been installed successfully.' });
      await loadData();
      setSelectedSkill(null);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Install Failed', message: apiErr.detail || 'Failed to install skill.' });
    }
  };

  const handleUninstall = async (skillId: string) => {
    try {
      await marketplaceApi.uninstall(skillId);
      addNotification({ type: 'success', title: 'Skill Uninstalled', message: 'The skill has been uninstalled.' });
      await loadData();
      setSelectedSkill(null);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Uninstall Failed', message: apiErr.detail || 'Failed to uninstall skill.' });
    }
  };

  const filteredSkills = skills.filter((s) => {
    if (selectedCategory !== 'All' && s.category !== selectedCategory) return false;
    if (search) {
      const q = search.toLowerCase();
      return s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q);
    }
    return true;
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
            <Store className="w-5 h-5 text-pink-700" />
          </div>
          <div>
            <div className="skeleton w-48 h-7 mb-1" />
            <div className="skeleton w-72 h-4" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card p-5 animate-pulse">
              <div className="skeleton w-32 h-5 mb-3" />
              <div className="skeleton w-full h-12 mb-3" />
              <div className="skeleton w-24 h-4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
            <Store className="w-5 h-5 text-pink-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Skill Marketplace</h1>
            <p className="text-sm text-gray-500 mt-0.5">Extend your platform with powerful consolidation skills</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="card p-4 border-red-200 bg-red-50">
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={loadData} className="ml-auto text-xs font-medium underline">Retry</button>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('catalog')}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium transition-colors',
              activeTab === 'catalog' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
            )}
          >
            Catalog ({skills.length})
          </button>
          <button
            onClick={() => setActiveTab('installed')}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium transition-colors',
              activeTab === 'installed' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
            )}
          >
            Installed ({installed.length})
          </button>
        </div>

        {/* Search */}
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search skills..."
            className="input-field text-sm pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {activeTab === 'catalog' && (
        <div className="flex gap-6">
          {/* Category Sidebar */}
          <div className="w-48 flex-shrink-0">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Categories</h3>
            <div className="space-y-0.5">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={clsx(
                    'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors',
                    selectedCategory === cat
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50',
                  )}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Skill Grid */}
          <div className="flex-1">
            {filteredSkills.length === 0 ? (
              <div className="card py-16 text-center">
                <Store className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No skills found matching your criteria.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredSkills.map((skill) => (
                  <div
                    key={skill.id}
                    className="card p-5 hover:shadow-card-hover transition-shadow cursor-pointer"
                    onClick={() => setSelectedSkill(skill)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900">{skill.name}</h3>
                        {skill.is_official && (
                          <span title="Official"><Shield className="w-3.5 h-3.5 text-blue-500" /></span>
                        )}
                        {skill.is_certified && (
                          <span title="Certified"><Award className="w-3.5 h-3.5 text-purple-500" /></span>
                        )}
                      </div>
                      {skill.is_installed && (
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 line-clamp-2 mb-3">{skill.description}</p>
                    <div className="flex items-center justify-between">
                      <span className={clsx(
                        'badge text-xs',
                        categoryConfig[skill.category]?.bg || 'bg-gray-100',
                        categoryConfig[skill.category]?.text || 'text-gray-700',
                      )}>
                        {skill.category}
                      </span>
                      <StarRating rating={skill.rating} />
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      <span>v{skill.version}</span>
                      <span>{skill.install_count.toLocaleString()} installs</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'installed' && (
        <div className="space-y-3">
          {installed.length === 0 ? (
            <div className="card py-16 text-center">
              <Download className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <h3 className="text-base font-medium text-gray-900">No Skills Installed</h3>
              <p className="text-sm text-gray-500 mt-1">Browse the catalog to install skills.</p>
            </div>
          ) : (
            installed.map((skill) => (
              <div key={skill.id} className="card p-4 flex items-center justify-between hover:shadow-card-hover transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-gray-900">{skill.name}</h3>
                      <span className="text-xs text-gray-400">v{skill.version}</span>
                      {skill.is_official && (
                        <span className="badge bg-blue-50 text-blue-700 text-[10px] gap-0.5">
                          <Shield className="w-2.5 h-2.5" /> Official
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{skill.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleUninstall(skill.skill_id)}
                  className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1 inline" />
                  Uninstall
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Skill Detail Modal */}
      {selectedSkill && (
        <SkillDetailModal
          skill={selectedSkill}
          onClose={() => setSelectedSkill(null)}
          onInstall={handleInstall}
          onUninstall={handleUninstall}
        />
      )}
    </div>
  );
}
